import {
  CONTRIBUTION_KEYS,
  PERMISSION_CAPABILITIES,
  type PluginManifest,
  type PermissionCapability,
} from './manifest';

export type ManifestValidation =
  | { ok: true; manifest: PluginManifest }
  | { ok: false; errors: string[] };

// Permissive semver: major.minor.patch with optional -prerelease / +build.
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
// Reverse-DNS-ish: dot/hyphen separated lowercase alphanumeric segments, 2+ segments.
const PLUGIN_ID = /^[a-z0-9]+([.-][a-z0-9]+)+$/;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Validate an untrusted manifest object. Returns every problem found so an
 * author sees all of them at once. Structural only — semver RANGE satisfaction
 * against the running host (engines.slate) is enforced by the runtime later.
 */
export function validateManifest(input: unknown): ManifestValidation {
  const errors: string[] = [];

  if (!isObject(input)) {
    return { ok: false, errors: ['manifest must be a JSON object'] };
  }

  if (!nonEmptyString(input.id)) errors.push('id is required (non-empty string)');
  else if (!PLUGIN_ID.test(input.id)) errors.push('id must be reverse-DNS (e.g. "acme.rust-tools")');

  if (!nonEmptyString(input.name)) errors.push('name is required (non-empty string)');
  if (!nonEmptyString(input.publisher)) errors.push('publisher is required (non-empty string)');

  if (!nonEmptyString(input.version)) errors.push('version is required (non-empty string)');
  else if (!SEMVER.test(input.version)) errors.push('version must be valid semver (e.g. "1.2.0")');

  const engines = input.engines;
  if (!isObject(engines) || !nonEmptyString(engines.slate)) {
    errors.push('engines.slate is required (a semver range string)');
  }

  if (input.permissions !== undefined) {
    if (!Array.isArray(input.permissions)) {
      errors.push('permissions must be an array');
    } else {
      input.permissions.forEach((p, i) => {
        if (!isObject(p)) {
          errors.push(`permissions[${i}] must be an object`);
          return;
        }
        if (!PERMISSION_CAPABILITIES.includes(p.capability as PermissionCapability)) {
          errors.push(`permissions[${i}].capability "${String(p.capability)}" is not a known capability`);
        }
        if (!nonEmptyString(p.reason)) {
          errors.push(`permissions[${i}].reason is required (explain why the plugin needs it)`);
        }
        if (p.capability === 'net') {
          if (!Array.isArray(p.domains) || p.domains.length === 0) {
            errors.push(`permissions[${i}] (net) must list at least one domain in "domains"`);
          }
        }
      });
    }
  }

  if (input.contributes !== undefined) {
    if (!isObject(input.contributes)) {
      errors.push('contributes must be an object');
    } else {
      for (const key of Object.keys(input.contributes)) {
        if (!(CONTRIBUTION_KEYS as readonly string[]).includes(key)) {
          errors.push(`contributes.${key} is not a known contribution point`);
        }
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, manifest: input as unknown as PluginManifest };
}
